import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchMentorRequests, fetchDevSuggestions } from "../lib/api";
import { Inbox, Send, Sparkles, AlertTriangle, ArrowRight } from "lucide-react";

/**
 * Action-oriented card: tells the current persona exactly what's on their plate
 * right now (pending mentor requests, pending suggestions, top risk).
 * Each row is clickable and routes to the relevant tab.
 */
export default function NextActionsCard({ persona, role, highRiskCount = 0, attritionTabPath, mentorTabPath, outboxTabPath, inboxTabPath }) {
  const nav = useNavigate();
  const empId = persona?.employee_id;
  const [counts, setCounts] = useState({ inbox: 0, outbox: 0, suggestionsInbox: 0, suggestionsOutbox: 0 });

  useEffect(() => {
    if (!empId) return;

    // Inbox: pending mentor requests
    //   - employee/manager: where they are the mentor
    //   - HR: ALL pending org-wide (org-level queue)
    const inboxFilter = role === "hr"
      ? { status: "pending" }
      : { mentor_id: empId, status: "pending" };
    fetchMentorRequests(inboxFilter)
      .then((d) => setCounts((c) => ({ ...c, inbox: d.items?.length || 0 })))
      .catch(() => {});

    // Outbox: my own pending mentor requests as mentee
    fetchMentorRequests({ mentee_id: empId, status: "pending" })
      .then((d) => setCounts((c) => ({ ...c, outbox: d.items?.length || 0 })))
      .catch(() => {});

    // Suggestions inbox: pending suggestions assigned to me as employee
    fetchDevSuggestions({ employee_id: empId, status: "pending" })
      .then((d) => setCounts((c) => ({ ...c, suggestionsInbox: d.items?.length || 0 })))
      .catch(() => {});

    // Suggestions outbox: pending suggestions I sent
    fetchDevSuggestions({ suggested_by_id: empId, status: "pending" })
      .then((d) => setCounts((c) => ({ ...c, suggestionsOutbox: d.items?.length || 0 })))
      .catch(() => {});
  }, [empId, role]);

  const rows = [];

  // Employee-specific rows
  if (role === "employee") {
    rows.push({
      icon: Inbox,
      title: "Bana gelen mentor talepleri",
      desc: counts.inbox > 0 ? `${counts.inbox} talep yanıtını bekliyor` : "Şu an yeni bir talep yok",
      count: counts.inbox,
      cta: "Talepleri yönet",
      onClick: () => inboxTabPath && nav(inboxTabPath),
      accent: "sky",
      urgent: counts.inbox > 0,
    });
    rows.push({
      icon: Sparkles,
      title: "Bana yapılan gelişim önerileri",
      desc: counts.suggestionsInbox > 0 ? `${counts.suggestionsInbox} öneri inceleme bekliyor` : "Bekleyen öneri yok",
      count: counts.suggestionsInbox,
      cta: "Önerileri gör",
      onClick: () => inboxTabPath && nav(`${inboxTabPath}#suggestions`),
      accent: "violet",
      urgent: counts.suggestionsInbox > 0,
    });
    rows.push({
      icon: Send,
      title: "Gönderdiğim mentor talepleri",
      desc: counts.outbox > 0 ? `${counts.outbox} talebin yanıt bekliyor` : "Bekleyen talebin yok",
      count: counts.outbox,
      cta: "Takip et",
      onClick: () => inboxTabPath && nav(inboxTabPath),
      accent: "emerald",
      urgent: false,
    });
  }

  // Manager-specific rows
  if (role === "manager") {
    rows.push({
      icon: AlertTriangle,
      title: "Yüksek risk taşıyan ekip üyesi",
      desc: highRiskCount > 0 ? `${highRiskCount} kişi acil görüşme gerektiriyor` : "Şu an yüksek riskli üye yok",
      count: highRiskCount,
      cta: "Risk radarını aç",
      onClick: () => attritionTabPath && nav(attritionTabPath),
      accent: "rose",
      urgent: highRiskCount > 0,
    });
    rows.push({
      icon: Inbox,
      title: "Ekibimden gelen mentor talepleri",
      desc: counts.inbox > 0 ? `${counts.inbox} talep onayını bekliyor` : "Yeni talep yok",
      count: counts.inbox,
      cta: "Talepleri incele",
      onClick: () => mentorTabPath && nav(mentorTabPath),
      accent: "sky",
      urgent: counts.inbox > 0,
    });
    rows.push({
      icon: Send,
      title: "Gönderdiğim gelişim önerileri",
      desc: counts.suggestionsOutbox > 0 ? `${counts.suggestionsOutbox} öneri ekipten yanıt bekliyor` : "Bekleyen öneri yok",
      count: counts.suggestionsOutbox,
      cta: "Önerilerimi gör",
      onClick: () => outboxTabPath && nav(outboxTabPath),
      accent: "amber",
      urgent: false,
    });
  }

  // HR-specific rows
  if (role === "hr") {
    rows.push({
      icon: Inbox,
      title: "Bekleyen mentor talepleri (kurum)",
      desc: counts.inbox > 0 ? `${counts.inbox} talep İK onayını bekliyor` : "Tüm talepler işlendi",
      count: counts.inbox,
      cta: "Onay kuyruğunu aç",
      onClick: () => mentorTabPath && nav(mentorTabPath),
      accent: "sky",
      urgent: counts.inbox > 0,
    });
    rows.push({
      icon: Send,
      title: "Gönderdiğim gelişim önerileri",
      desc: counts.suggestionsOutbox > 0 ? `${counts.suggestionsOutbox} öneri çalışan yanıtını bekliyor` : "Bekleyen öneri yok",
      count: counts.suggestionsOutbox,
      cta: "Takip et",
      onClick: () => outboxTabPath && nav(outboxTabPath),
      accent: "amber",
      urgent: false,
    });
    rows.push({
      icon: AlertTriangle,
      title: "Kurumsal ayrılma riski",
      desc: highRiskCount > 0 ? `${highRiskCount} yüksek riskli çalışan` : "Yüksek risk yok",
      count: highRiskCount,
      cta: "Risk panelini aç",
      onClick: () => attritionTabPath && nav(attritionTabPath),
      accent: "rose",
      urgent: highRiskCount > 0,
    });
  }

  const accentMap = {
    sky: "border-sky-200 bg-sky-50/60 text-sky-700",
    rose: "border-rose-200 bg-rose-50/60 text-rose-700",
    amber: "border-amber-200 bg-amber-50/60 text-amber-700",
    emerald: "border-emerald-200 bg-emerald-50/60 text-emerald-700",
    violet: "border-violet-200 bg-violet-50/60 text-violet-700",
  };
  const iconBg = {
    sky: "bg-sky-100 text-sky-700",
    rose: "bg-rose-100 text-rose-700",
    amber: "bg-amber-100 text-amber-700",
    emerald: "bg-emerald-100 text-emerald-700",
    violet: "bg-violet-100 text-violet-700",
  };

  return (
    <div className="card-surface p-6" data-testid="next-actions-card">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="label-eyebrow text-slate-500 mb-1">Bugün Sırada Ne Var?</div>
          <h3 className="font-display text-lg font-semibold tracking-tight">Önceliklendirilmiş Aksiyonlar</h3>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {rows.map((r, idx) => {
          const Icon = r.icon;
          return (
            <button
              key={idx}
              type="button"
              onClick={r.onClick}
              data-testid={`next-action-${role}-${idx}`}
              className={`text-left rounded-xl border ${accentMap[r.accent]} p-4 hover:shadow-sm transition group relative overflow-hidden`}
            >
              {r.urgent && (
                <span className="absolute top-2 right-2 w-2 h-2 rounded-full bg-rose-500 animate-pulse" aria-label="Bekleyen aksiyon" />
              )}
              <div className="flex items-start gap-3">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${iconBg[r.accent]} shrink-0`}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-slate-800 flex items-baseline gap-2">
                    <span className="truncate">{r.title}</span>
                    {r.count > 0 && (
                      <span className="text-base font-bold tabular-nums">{r.count}</span>
                    )}
                  </div>
                  <div className="text-xs text-slate-600 mt-1 leading-snug">{r.desc}</div>
                  <div className="text-[0.7rem] mt-2 font-medium inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                    {r.cta} <ArrowRight className="w-3 h-3" />
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
